import { MeiliSearch } from "meilisearch";
import { MeilisearchProduct } from "../types-var/product";
import { BlogArticle } from "../types-var/blog"; // adjust path as needed

// Export the type so it can be imported elsewhere
export type SortOption =
  | "popularity:desc"
  | "price:asc"
  | "price:desc"
  | "name:asc"
  | "name:desc";

export interface SearchOptions {
  sort?: SortOption[];
  filter?: string[];
  page?: number;
  hitsPerPage?: number;
}

const client = new MeiliSearch({
  host: process.env.REACT_APP_MEILISEARCH_HOST || "https://search.evefant.com",
  apiKey:
    process.env.REACT_APP_MEILISEARCH_API_KEY ||
    "ieerJ0Mumuusc3Tirvql5VJRwpAWxBXtvGF3mrdLTg0",
});

const SORT_MAPPING: Record<SortOption, string[]> = {
  "popularity:desc": [], // Empty array means no sorting, use default order
  "price:asc": ["price.regPrice:asc"],
  "price:desc": ["price.regPrice:desc"],
  "name:asc": ["name:asc"],
  "name:desc": ["name:desc"],
};

export const configureMeilisearchIndex = async () => {
  try {
    const settings = {
      filterableAttributes: [
        "stock",
        "partner.name",
        "sku",
        "name",
        "price.regPrice",
        "price.discount",
        "isCustomizable",
        "type",
        "attributes.name",
        "attributes.values.name",
        "categories.id",
        "categories.url",
        "categories.level",
        "categories.parent.id",
        "categories.parent.url",
        "categories.name",
        "categories.parent.name",
        "categories.parent.level",
        "categories.parent.parent.url", // Add this
        "categories.parent.parent.parent.url", // Add this
        "categories.parent.parent.name",
        "categories.parent.parent.parent.name",
        "categoryIds",
        "categoryPath",
        "categories.id",
        "categories.parentId",
        "categories.level",
        "categories.path",
      ],
      sortableAttributes: ["name", "price.regPrice"],
      searchableAttributes: [
        "name",
        "description",
        "sku",
        "partner.name",
        "attributes.values.name",
        "categories.name",
        "categories.parent.name",
      ],
      distinctAttribute: null,
      typoTolerance: {
        enabled: true,
        minWordSizeForTypos: {
          oneTypo: 4,
          twoTypos: 8,
        },
        disableOnAttributes: ["attributes.values.name"],
      },
    };

    console.log("Updating Meilisearch settings...");
    await client.index("products").updateSettings(settings);
    console.log("Settings updated successfully");

    // Verify settings
    const updatedSettings = await client.index("products").getSettings();
    console.log("Current settings:", updatedSettings);
  } catch (error) {
    console.error("Failed to configure Meilisearch:", error);
  }
};

export const searchProducts = async (
  query: string,
  options: SearchOptions = {}
) => {
  try {
    const sortValue = options.sort?.[0]
      ? SORT_MAPPING[options.sort[0]]
      : undefined;

    const searchParams = {
      q: query,
      hitsPerPage: options.hitsPerPage || 24,
      page: options.page || 1,
      ...(sortValue?.length ? { sort: sortValue } : {}), // Only include sort if we have values
      ...(options.filter?.length ? { filter: options.filter } : {}),
    };

    console.log("Search request:", { query, params: searchParams });
    const results = await client.index("products").search(query, searchParams);
    console.log("Search response:", {
      hits: results.hits.length,
      sort: sortValue,
      firstPrice: results.hits[0]?.price.regPrice,
    });

    // Verify data structure of first result
    if (results.hits.length > 0) {
      const firstProduct = results.hits[0];
      console.log("Product delivery zones:", {
        partner: firstProduct.partner,
        originZones: firstProduct.partner?.originZones,
        deliveryFeeZone: firstProduct.partner?.originZones?.deliveryFeeZone,
        deliveryLeadTimeZone:
          firstProduct.partner?.originZones?.deliveryLeadTimeZone,
      });
    }

    return results;
  } catch (error) {
    console.error("Meilisearch error:", error);
    throw error;
  }
};

// Add a helper function for category-based search
export const searchProductsByCategory = async (
  categoryUrl: string,
  options: SearchOptions = {}
) => {
  try {
    const searchParams = {
      ...options,
      filter: [`categories.url = "${categoryUrl}"`, ...(options.filter || [])],
    };

    const results = await client.index("products").search("", searchParams);
    return results;
  } catch (error) {
    console.error("Meilisearch category search error:", error);
    throw error;
  }
};

interface SearchResponse<T> {
  hits: T[];
  totalHits: number;
  totalPages: number;
  page: number;
  hitsPerPage: number;
}

export const getProductsByCategory = async (
  { id, url, level }: { id: number; url: string; level: number },
  options: SearchOptions = {}
): Promise<SearchResponse<MeilisearchProduct>> => {
  try {
    // Use the filter conditions from options if they exist
    const searchParams = {
      q: "",
      hitsPerPage: options.hitsPerPage || 24,
      page: options.page || 1,
      ...(options.sort?.length ? { sort: SORT_MAPPING[options.sort[0]] } : {}),
      // Use the filter conditions passed in options instead of overriding them
      ...(options.filter
        ? { filter: options.filter }
        : { filter: `categoryIds = ${id}` }),
    };

    console.log("Category search request:", {
      categoryId: id,
      level,
      filter: searchParams.filter,
      sort: searchParams.sort,
    });

    const results = await client.index("products").search("", searchParams);

    return {
      hits: results.hits as MeilisearchProduct[],
      totalHits: results.totalHits,
      totalPages: results.totalPages,
      page: results.page,
      hitsPerPage: results.hitsPerPage,
    };
  } catch (error) {
    console.error("Category products fetch error:", error);
    throw error;
  }
};

export const searchBlogArticles = async (query: string, options: any) => {
  // If the sort option is popularity, remove the sort parameter so that
  // the default ranking order is used.
  if (options.sort && options.sort[0] === "popularity:desc") {
    delete options.sort;
  }
  return client.index("blog_articles").search<BlogArticle>(query, options);
};
